import { api } from './api';

export interface StudentTopicInfo {
  nodeId: number;
  label: string;
}

export interface StudentTestSummary {
  id: number;
  name: string;
  scheduledDate: string;
  questionCount: number;
  timeLimitMinutes: number | null;
  status: 'UPCOMING' | 'PAST';
  myAttemptCount: number;
  topics: StudentTopicInfo[];
}

export interface StudentTestDetail {
  id: number;
  name: string;
  scheduledDate: string;
  questionCount: number;
  timeLimitMinutes: number | null;
  status: 'UPCOMING' | 'PAST';
  topics: Array<StudentTopicInfo & { hasContent: boolean; hasPracticeQuiz: boolean }>;
}

export interface OptionItem {
  index: number;
  text: string;
}

export interface QuestionItem {
  snapshotQuestionId: number;
  questionText: string;
  questionType: string;
  options: OptionItem[];
}

export interface StartAttemptResponse {
  attemptId: number;
  testId: number;
  testName: string;
  timeLimitMinutes: number | null;
  startedAt: string;
  questions: QuestionItem[];
}

export interface SubmitAnswerItem {
  snapshotQuestionId: number;
  selectedOptionIndex: number;
}

export interface SubmitAttemptResult {
  attemptId: number;
  scoreCorrect: number;
  scoreTotal: number;
  scorePercent: number;
  submittedAt: string;
}

export interface AttemptHistoryEntry {
  attemptId: number;
  attemptNumber: number;
  scoreCorrect: number;
  scoreTotal: number;
  scorePercent: number;
  submittedAt: string;
}

export interface OptionReplay {
  index: number;
  text: string;
  isCorrect: boolean;
}

export interface QuestionReplay {
  snapshotQuestionId: number;
  questionText: string;
  options: OptionReplay[];
  selectedOptionIndex: number | null;
  isCorrect: boolean;
  explanation: string | null;
}

export interface AttemptDetail {
  attemptId: number;
  scoreCorrect: number;
  scoreTotal: number;
  submittedAt: string;
  questions: QuestionReplay[];
}

export const listStudentTests = async (courseId: number): Promise<StudentTestSummary[]> => {
  const res = await api.get<{ data: StudentTestSummary[] }>(`/api/student/courses/${courseId}/tests`);
  return res.data.data;
};

export const getStudentTest = async (courseId: number, testId: number): Promise<StudentTestDetail> => {
  const res = await api.get<{ data: StudentTestDetail }>(`/api/student/courses/${courseId}/tests/${testId}`);
  return res.data.data;
};

export const startAttempt = async (courseId: number, testId: number): Promise<StartAttemptResponse> => {
  const res = await api.post<{ data: StartAttemptResponse }>(
    `/api/student/courses/${courseId}/tests/${testId}/start`,
    {},
  );
  return res.data.data;
};

export const submitAttempt = async (
  courseId: number,
  testId: number,
  attemptId: number,
  answers: SubmitAnswerItem[],
): Promise<SubmitAttemptResult> => {
  const res = await api.post<{ data: SubmitAttemptResult }>(
    `/api/student/courses/${courseId}/tests/${testId}/attempts/${attemptId}/submit`,
    { answers },
  );
  return res.data.data;
};

export const listAttempts = async (courseId: number, testId: number): Promise<AttemptHistoryEntry[]> => {
  const res = await api.get<{ data: AttemptHistoryEntry[] }>(
    `/api/student/courses/${courseId}/tests/${testId}/attempts`,
  );
  return res.data.data;
};

export const getAttemptDetail = async (
  courseId: number,
  testId: number,
  attemptId: number,
): Promise<AttemptDetail> => {
  const res = await api.get<{ data: AttemptDetail }>(
    `/api/student/courses/${courseId}/tests/${testId}/attempts/${attemptId}`,
  );
  return res.data.data;
};
